/**
 * Dashboard — editorial home of Network-iA.
 *
 * Layout :
 *   • TopBar (sticky)  : lang switcher + avatar user · sidebar fait le reste
 *   • Header editorial : kicker + greeting display + sub
 *   • Bento grid       : tiles vivantes — pas de menu redondant
 *
 * Toutes les chaînes UI passent par i18next (12 langues).
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserSearch,
  Palette,
  ArrowRight,
  Flame,
  Users,
  Activity,
  BellRing,
  TrendingUp,
  Compass,
  Wand2,
  BookOpen,
  Sparkles,
  Lightbulb,
  User as UserIcon,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useProspectStats } from '@/hooks/useProspect';
import { useIdentity } from '@/hooks/useIdentity';
import { useContent } from '@/hooks/useContent';
import { useVisualLibrary } from '@/hooks/useVisuals';
import { useDiagnostic } from '@/hooks/useDiagnostic';
import { Reveal } from '@/components/Reveal';
import { openGuidedTour, hasSeenTour } from '@/components/GuidedTour';
import { InterviewInviteCard } from '@/components/InterviewInviteCard';
import { DailyBriefing } from '@/components/DailyBriefing';
import { SectorEmptyBanner } from '@/components/SectorEmptyBanner';
import { TrialBanner } from '@/components/TrialBanner';
import { CmStartChecklist } from '@/components/CmStartChecklist';
import { AutomationsTile, CalendarTile, InboxTile } from '@/components/dashboard/MrTiles';
import { STAGE_META, STAGE_ORDER } from '@/lib/prospectMeta';

export default function DashboardPage() {
  // Visite guidée : proposée une seule fois, à la première ouverture de l'app.
  const tourShown = useRef(false);

  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: stats } = useProspectStats();
  const { kit } = useIdentity();
  const { plan } = useContent();
  const { data: library } = useVisualLibrary();
  const { diagnostic, isLoading: diagnosticLoading } = useDiagnostic();

  const firstName = user?.full_name?.split(' ')[0] ?? user?.display_name ?? 'Partenaire';
  // Lot 7 (2026-10-29) — espace M&R (origine CRM) : accueil recentré sur les réseaux, les automatisations et les conversations.
  const mrSpace = user?.origin === 'mr_crm';
  const total = stats?.total ?? 0;
  const hot = stats?.byTemperature?.hot ?? 0;
  const due = stats?.followUpsDue ?? 0;
  const audits7d = stats?.auditsLast7Days ?? 0;
  const firstIdea = plan?.post_ideas?.[0];
  const lastVisual = library?.[0];
  const lastVisualMeta = lastVisual
    ? (lastVisual.layers ?? []).find((l) => l && (l as { __meta?: boolean }).__meta)
    : null;

  // Première ouverture de l'app par ce membre : on propose la visite guidée
  // une seule fois (le drapeau est posé dès qu'elle est fermée ou terminée).
  useEffect(() => {
    if (tourShown.current || !user?.id) return;
    if (hasSeenTour(user.id)) return;
    tourShown.current = true;
    const id = window.setTimeout(openGuidedTour, 900);
    return () => window.clearTimeout(id);
  }, [user?.id]);

  return (
    <div className="mx-auto max-w-[1320px] px-4 pb-12 pt-4 sm:px-6 lg:px-10">
      {/* ── Top bar : profil only (langue maintenant dans la sidebar) ──── */}
      <div className="mb-6 flex items-center justify-end gap-2.5">
        <button
          onClick={() => navigate('/profil')}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/65 bg-white/65 text-ink/65 backdrop-blur-xl transition hover:border-violet-300/50 hover:bg-white/85 hover:text-ink"
          aria-label={t('nav.profile')}
        >
          <UserIcon size={15} />
        </button>
      </div>

      {/* ── Editorial header ─────────────────────────────────────────────── */}
      <Reveal>
        <header className="mb-9">
          <p className="kicker text-pink">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-pink/60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-gradient-to-br from-blue to-pink" />
            </span>
            {t(mrSpace ? 'dashboard.mr.kicker' : 'dashboard.kicker')} ·{' '}
            {new Date().toLocaleDateString(i18n.language, {
              weekday: 'long',
              day: '2-digit',
              month: 'long',
            })}
          </p>
          <h1 className="mt-3 font-display text-[clamp(32px,4.5vw,52px)] font-800 leading-[1.02] tracking-[-0.035em]">
            {t('dashboard.greeting', { name: '' }).replace('.', '').trim()}{' '}
            <span className="text-grad">{firstName}</span>
            <span className="text-ink">.</span>
          </h1>
          <p className="mt-3 max-w-2xl text-[15px] leading-[1.55] text-ink/55">
            {t(mrSpace ? 'dashboard.mr.subtitle' : 'dashboard.subtitle')}
          </p>
        </header>
      </Reveal>

      {/* Trial banner — only renders for users on a 15-day invited trial */}
      <TrialBanner />

      {/* Sector empty banner — only renders if user has no sectors yet */}
      <SectorEmptyBanner />

      {/* Mise en route de la community manager (lot 6, 2026-10-29) — compte CRM M&R, module Automatisations ouvert */}
      <CmStartChecklist />

      {/* ── Bento grid ───────────────────────────────────────────────────── */}
      {mrSpace ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-12 sm:gap-5" data-dash="mr">
          <Reveal delay={0.05} className="sm:col-span-6 lg:col-span-4"><InboxTile /></Reveal>
          <Reveal delay={0.1} className="sm:col-span-6 lg:col-span-4"><AutomationsTile /></Reveal>
          <Reveal delay={0.14} className="sm:col-span-12 lg:col-span-4"><CalendarTile /></Reveal>
          <Reveal delay={0.18} className="sm:col-span-12 lg:col-span-4"><ContentTile idea={firstIdea} onClick={() => navigate('/contenu')} t={t} /></Reveal>
          <Reveal delay={0.22} className="sm:col-span-6 lg:col-span-4"><IdentityTile kit={kit} onClick={() => navigate('/identite')} t={t} /></Reveal>
          <Reveal delay={0.26} className="sm:col-span-6 lg:col-span-4">
            <StudioTile lastVisualThumb={lastVisualMeta?.thumbnail} lastVisualTitle={lastVisualMeta?.title} count={library?.length ?? 0} onClickCreate={() => navigate('/studio')} onClickLibrary={() => navigate('/bibliotheque')} t={t} />
          </Reveal>
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-12 sm:gap-5" data-dash="mars">
        {/* Mission du jour — hero */}
        <Reveal delay={0.05} className="sm:col-span-12 lg:col-span-8">
          <DailyBriefing />
        </Reveal>

        {/* Pipeline funnel */}
        <Reveal delay={0.1} className="sm:col-span-12 lg:col-span-4">
          <PipelineTile
            total={total}
            byStage={stats?.byStage ?? {}}
            onClick={() => navigate('/prospects')}
          />
        </Reveal>

        {/* KPI row × 4 */}
        <Reveal delay={0.14} className="sm:col-span-6 lg:col-span-3">
          <KpiTile
            icon={<Users size={15} />}
            label={t('dashboard.prospects')}
            value={total}
            tint="#3b82f6"
          />
        </Reveal>
        <Reveal delay={0.18} className="sm:col-span-6 lg:col-span-3">
          <KpiTile
            icon={<Flame size={15} />}
            label={t('dashboard.hots')}
            value={hot}
            tint="#ec4899"
          />
        </Reveal>
        <Reveal delay={0.22} className="sm:col-span-6 lg:col-span-3">
          <KpiTile
            icon={<BellRing size={15} />}
            label={t('dashboard.followupsDue')}
            value={due}
            tint="#f59e0b"
            onClick={() => navigate('/relances')}
          />
        </Reveal>
        <Reveal delay={0.26} className="sm:col-span-6 lg:col-span-3">
          <KpiTile
            icon={<Activity size={15} />}
            label={t('dashboard.analyses7d')}
            value={audits7d}
            tint="#a855f7"
          />
        </Reveal>

        {/* Audit cinematic CTA */}
        <Reveal delay={0.3} className="sm:col-span-12 lg:col-span-7">
          <AuditTile onClick={() => navigate('/audit')} t={t} />
        </Reveal>

        {/* Idée du jour */}
        <Reveal delay={0.34} className="sm:col-span-12 lg:col-span-5">
          <ContentTile idea={firstIdea} onClick={() => navigate('/contenu')} t={t} />
        </Reveal>

        {/* Identité */}
        <Reveal delay={0.38} className="sm:col-span-12 lg:col-span-4">
          <IdentityTile kit={kit} onClick={() => navigate('/identite')} t={t} />
        </Reveal>

        {/* Studio */}
        <Reveal delay={0.42} className="sm:col-span-12 lg:col-span-4">
          <StudioTile
            lastVisualThumb={lastVisualMeta?.thumbnail}
            lastVisualTitle={lastVisualMeta?.title}
            count={library?.length ?? 0}
            onClickCreate={() => navigate('/studio')}
            onClickLibrary={() => navigate('/bibliotheque')}
            t={t}
          />
        </Reveal>

        {/* Diagnostic */}
        <Reveal delay={0.46} className="sm:col-span-12 lg:col-span-4">
          {diagnostic || diagnosticLoading ? (
            <DiagnosticTile
              headline={diagnostic?.headline}
              positioning={diagnostic?.recommended_positioning}
              onClick={() => navigate('/diagnostic')}
              t={t}
            />
          ) : (
            <InterviewInviteCard />
          )}
        </Reveal>
      </div>
      )}
    </div>
  );
}

// ── Bento tile primitives ────────────────────────────────────────────────────

function PipelineTile({
  total,
  byStage,
  onClick,
}: {
  total: number;
  byStage: Partial<Record<string, number>>;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button onClick={onClick} data-tile="pipeline" className="card group h-full w-full p-6 text-left">
      <div className="mb-4 flex items-center justify-between">
        <span className="kicker text-violet">
          <TrendingUp size={11} /> {t('dashboard.pipeline')}
        </span>
        <ArrowRight
          size={15}
          className="text-ink/30 transition group-hover:translate-x-0.5 group-hover:text-pink"
        />
      </div>
      <div className="flex items-baseline gap-2">
        <p className="font-display text-[56px] font-800 leading-none tracking-[-0.035em]">
          {total}
        </p>
        <p className="text-[12.5px] text-ink/55">{t('dashboard.prospectsTracked')}</p>
      </div>
      <div className="mt-5 flex h-2.5 overflow-hidden rounded-full bg-ink/[0.05]">
        {STAGE_ORDER.map((s) => {
          const n = byStage[s] ?? 0;
          if (n === 0 || total === 0) return null;
          return (
            <div
              key={s}
              style={{ width: `${(n / total) * 100}%`, background: STAGE_META[s].color }}
              title={`${t(STAGE_META[s].labelKey)} : ${n}`}
            />
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-3.5 gap-y-1.5">
        {STAGE_ORDER.slice(0, 4).map((s) => {
          const n = byStage[s] ?? 0;
          if (n === 0) return null;
          return (
            <span key={s} className="flex items-center gap-1.5 text-[11px] font-600 text-ink/60">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: STAGE_META[s].color }}
              />
              {t(STAGE_META[s].labelKey)}
              <span className="font-700 text-ink/85">{n}</span>
            </span>
          );
        })}
      </div>
    </button>
  );
}

function KpiTile({
  icon,
  label,
  value,
  tint,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tint: string;
  onClick?: () => void;
}) {
  const inner = (
    <>
      <div
        className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-25 blur-2xl"
        style={{ background: tint }}
      />
      <div className="flex items-center justify-between">
        <span
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-white"
          style={{
            background: `linear-gradient(135deg, ${tint}, ${tint}cc)`,
            boxShadow: `0 8px 18px -8px ${tint}`,
          }}
        >
          {icon}
        </span>
        {onClick && (
          <ArrowRight size={12} className="text-ink/25 transition group-hover:translate-x-0.5 group-hover:text-pink" />
        )}
      </div>
      <p className="mt-4 font-display text-[40px] font-800 leading-none tracking-[-0.03em]">
        {value}
      </p>
      <p className="mt-1.5 text-[12.5px] font-500 text-ink/55">{label}</p>
    </>
  );
  const cls =
    'card group relative h-full w-full overflow-hidden p-5 text-left' +
    (onClick ? ' cursor-pointer' : '');
  return onClick ? (
    <button onClick={onClick} className={cls}>
      {inner}
    </button>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

function AuditTile({ onClick, t }: { onClick: () => void; t: (k: string) => string }) {
  return (
    <button
      onClick={onClick}
      className="group relative h-full w-full overflow-hidden rounded-3xl text-left"
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, #0A1226 0%, #1a1f3d 35%, #3b1664 70%, #6b1a5a 100%)',
        }}
      />
      <div
        className="pointer-events-none absolute -right-12 -top-12 h-56 w-56 rounded-full opacity-60 blur-3xl"
        style={{ background: 'radial-gradient(circle, #ec4899, transparent 70%)' }}
      />
      <div
        className="pointer-events-none absolute -bottom-10 -left-10 h-48 w-48 rounded-full opacity-50 blur-3xl"
        style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }}
      />
      {/* Shine sweep removed for perf — was 6s infinite repaint of full tile */}
      <div className="relative flex h-full flex-col justify-between gap-5 p-7 sm:p-9">
        <div>
          <span className="kicker text-pink-glow">
            <Sparkles size={11} /> {t('dashboard.audit')}
          </span>
          <h3 className="mt-3 max-w-md font-display text-[clamp(22px,2.7vw,32px)] font-800 leading-[1.08] tracking-[-0.025em] text-white">
            {t('dashboard.auditDesc')}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-[13px] font-700 text-ink shadow-lg transition group-hover:scale-[1.02]">
            <UserSearch size={14} /> {t('dashboard.auditCta')}
          </span>
        </div>
      </div>
    </button>
  );
}

function ContentTile({
  idea,
  onClick,
  t,
}: {
  idea: { title: string; hook: string; platform: string; format: string } | undefined;
  onClick: () => void;
  t: (k: string) => string;
}) {
  return (
    <button onClick={onClick} className="card group h-full w-full p-6 text-left">
      <div className="mb-3 flex items-center justify-between">
        <span className="kicker text-violet">
          <Lightbulb size={11} /> {t('dashboard.content')}
        </span>
        <ArrowRight
          size={15}
          className="text-ink/30 transition group-hover:translate-x-0.5 group-hover:text-pink"
        />
      </div>
      {idea ? (
        <>
          <p className="font-display text-[18px] font-800 leading-[1.2] tracking-[-0.02em]">
            {idea.title}
          </p>
          <div className="mt-3.5 rounded-2xl bg-violet/[0.06] p-4">
            <p className="font-mono text-[9.5px] font-700 uppercase tracking-[0.14em] text-violet">
              Accroche
            </p>
            <p className="mt-1 text-[13px] italic leading-[1.5] text-ink/75">« {idea.hook} »</p>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="rounded-full bg-ink/[0.05] px-2.5 py-0.5 font-mono text-[10px] font-700 uppercase tracking-[0.1em] text-ink/55">
              {idea.platform}
            </span>
            <span className="rounded-full bg-ink/[0.05] px-2.5 py-0.5 font-mono text-[10px] font-700 uppercase tracking-[0.1em] text-ink/55">
              {idea.format}
            </span>
          </div>
        </>
      ) : (
        <>
          <p className="font-display text-[17px] font-800 leading-[1.2] tracking-[-0.02em]">
            {t('dashboard.contentEmptyTitle')}
          </p>
          <p className="mt-2 text-[13px] leading-[1.5] text-ink/60">
            {t('dashboard.contentEmptyBody')}
          </p>
        </>
      )}
    </button>
  );
}

function IdentityTile({
  kit,
  onClick,
  t,
}: {
  kit: ReturnType<typeof useIdentity>['kit'];
  onClick: () => void;
  t: (k: string) => string;
}) {
  const palette = kit?.palettes[0];
  return (
    <button onClick={onClick} className="card group h-full w-full overflow-hidden p-5 text-left">
      <div className="mb-3 flex items-center justify-between">
        <span className="kicker text-blue">
          <Palette size={11} /> {t('dashboard.identity')}
        </span>
        <ArrowRight
          size={15}
          className="text-ink/30 transition group-hover:translate-x-0.5 group-hover:text-pink"
        />
      </div>
      {palette ? (
        <>
          <div className="mb-3 flex h-14 overflow-hidden rounded-2xl ring-1 ring-white/30">
            {palette.colors.map((c, i) => (
              <div key={i} className="flex-1" style={{ background: c }} />
            ))}
          </div>
          <p className="font-display text-[15px] font-800 tracking-[-0.015em] text-ink">
            {palette.name}
          </p>
          <p className="mt-1 text-[12px] text-ink/55">
            {kit.bios.instagram.length} bios IG · {kit.bios.tiktok.length} TT
          </p>
        </>
      ) : (
        <>
          <p className="font-display text-[16px] font-800 tracking-[-0.02em]">
            {t('dashboard.identityEmptyTitle')}
          </p>
          <p className="mt-1.5 text-[12.5px] text-ink/55">{t("dashboard.identityTeaser")}</p>
        </>
      )}
    </button>
  );
}

function StudioTile({
  lastVisualThumb,
  lastVisualTitle,
  count,
  onClickCreate,
  onClickLibrary,
  t,
}: {
  lastVisualThumb?: string;
  lastVisualTitle?: string;
  count: number;
  onClickCreate: () => void;
  onClickLibrary: () => void;
  t: (k: string) => string;
}) {
  return (
    <div className="card group h-full w-full overflow-hidden p-0">
      <div className="relative aspect-[16/10] bg-gradient-to-br from-pink/[0.10] via-violet/[0.06] to-blue/[0.08]">
        {lastVisualThumb ? (
          <img
            src={lastVisualThumb}
            alt={lastVisualTitle ?? 'Dernier visuel'}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Wand2 size={36} className="text-pink/45" />
          </div>
        )}
        <div className="absolute inset-x-3 bottom-3 flex items-center justify-between gap-2 text-[10.5px]">
          <span className="rounded-full bg-night/65 px-2.5 py-1 font-mono font-700 uppercase tracking-[0.1em] text-white backdrop-blur">
            {t('dashboard.studio')}
          </span>
          {count > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClickLibrary();
              }}
              className="rounded-full bg-white/95 px-2.5 py-1 font-700 text-ink/70 backdrop-blur transition hover:text-pink"
            >
              <BookOpen size={11} className="mr-1 inline" />
              {count}
            </button>
          )}
        </div>
      </div>
      <button onClick={onClickCreate} className="block w-full p-4 text-left">
        <p className="font-display text-[15px] font-800 tracking-[-0.015em] text-ink">
          {lastVisualTitle ?? t('dashboard.nextVisual')}
        </p>
        <p className="mt-1 inline-flex items-center gap-1 text-[12px] font-700 text-pink transition-transform group-hover:translate-x-0.5">
          {t('common.open')} <ArrowRight size={11} />
        </p>
      </button>
    </div>
  );
}

function DiagnosticTile({
  headline,
  positioning,
  onClick,
  t,
}: {
  headline?: string;
  positioning?: string;
  onClick: () => void;
  t: (k: string) => string;
}) {
  return (
    <button onClick={onClick} className="card group relative h-full w-full overflow-hidden p-5 text-left">
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-30 blur-2xl"
        style={{ background: 'radial-gradient(circle, #a855f7, transparent 70%)' }}
      />
      <div className="relative">
        <div className="mb-3 flex items-center justify-between">
          <span className="kicker text-violet">
            <Compass size={11} /> {t('dashboard.diagnostic')}
          </span>
          <ArrowRight
            size={15}
            className="text-ink/30 transition group-hover:translate-x-0.5 group-hover:text-pink"
          />
        </div>
        {positioning ? (
          <>
            <p className="font-mono text-[9.5px] font-700 uppercase tracking-[0.14em] text-ink/45">
              Positionnement
            </p>
            <p className="mt-1 font-display text-[16px] font-800 tracking-[-0.02em] text-grad">
              {positioning}
            </p>
            {headline && (
              <p className="mt-2.5 line-clamp-3 text-[12.5px] italic leading-[1.5] text-ink/60">
                {headline}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="font-display text-[16px] font-800 tracking-[-0.02em]">
              {t('dashboard.diagnosticTitle')}
            </p>
            <p className="mt-1.5 text-[12.5px] text-ink/55">
              {t('dashboard.diagnosticBody')}
            </p>
          </>
        )}
      </div>
    </button>
  );
}
